import { ArrowDownTrayIcon, DocumentDuplicateIcon } from '@heroicons/react/24/outline';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { useMatches, useNavigate, useParams } from 'react-router';
import AgentMarkdown from '@/components/agent/AgentMarkdown';
import { getCase } from '@/api/cases';
import { getComparisons } from '@/api/comparisons';
import { getPathways } from '@/api/pathways';
import { getSystem, getSystems } from '@/api/systems';
import { analysisStatusActions } from '@/stores/analysisStatusStore';
import { caseActions } from '@/stores/caseStore';
import useNodeStore, { nodeActions } from '@/stores/nodeStore';
import usePathwayStore from '@/stores/pathwayStore';
import { resourcesActions } from '@/stores/resourcesStore';
import useSystemStore, { systemActions } from '@/stores/systemStore';
import { useIsFeatureEnabled } from '@/stores/settingsStore';
import useAgentStore, { agentActions } from '@/stores/agentStore';
import { toast } from '@/utils/toast';
import BetaBadge from '@/components/agent/BetaBadge';

// Tool names that mutate pathway data and need an immediate list refresh.
const PATHWAY_MUTATING_ACTIONS = new Set([
  'create_pathway_from_template',
  'build_pathway',
  'set_node_params',
  'set_anchor',
  'balance_case',
  'analyze_case',
  'create_comparison',
  'add_node',
  'connect_nodes',
  'remove_node',
]);

// Tool names that mutate system data and need a systems list / open-system refresh.
const SYSTEM_MUTATING_ACTIONS = new Set([
  'create_system_from_template',
  'create_systems_batch',
  'set_system_params',
  'set_solar_generator',
  'set_wind_generator',
  'set_storage_generator',
  'set_thermal_generator',
  'set_pathway_powerplant',
  'delete_generator',
  'run_system',
]);

// ---------------------------------------------------------------------------
// Navigation context
// ---------------------------------------------------------------------------

function buildNavigationContext(matches) {
  const params = matches.reduce((acc, m) => ({ ...acc, ...(m.params ?? {}) }), {});
  const lastMatch = matches[matches.length - 1];

  return {
    route: lastMatch?.id ?? lastMatch?.pathname ?? 'unknown',
    pathway_id: params.pathwayId ?? null,
    case_id: params.caseId ?? null,
    node_id: useNodeStore.getState()?.caseNode?.node_id ?? null,
  };
}

// ---------------------------------------------------------------------------
// Progress indicators for write actions
// ---------------------------------------------------------------------------

const PROGRESS_ACTIONS = new Set([
  'create_pathway_from_template',
  'build_pathway',
  'set_node_params',
  'set_anchor',
  'balance_case',
  'analyze_case',
  'create_comparison',
  'generate_report',
  // System actions (BTM asset agent + future hydrogen / power dispatch)
  'create_system_from_template',
  'create_systems_batch',
  'set_system_params',
  'set_solar_generator',
  'set_wind_generator',
  'set_storage_generator',
  'set_thermal_generator',
  'set_pathway_powerplant',
  'delete_generator',
  'run_system',
  'get_system_results',
  'compare_systems',
  'load_skill',
]);

const PLURAL_LABEL = {
  create_pathway_from_template: 'Creating pathways',
  build_pathway: 'Building pathways',
  balance_case: 'Balancing pathways',
  analyze_case: 'Running analyses',
  set_node_params: 'Updating parameters',
  create_system_from_template: 'Creating systems',
  run_system: 'Running optimizations',
};

function progressLabel(actionType) {
  switch (actionType) {
    case 'create_pathway_from_template':
      return 'Creating pathway';
    case 'build_pathway':
      return 'Building pathway';
    case 'balance_case':
      return 'Balancing pathway';
    case 'analyze_case':
      return 'Running analysis';
    case 'set_node_params':
      return 'Updating parameters';
    case 'set_anchor':
      return 'Setting basis';
    case 'create_comparison':
      return 'Creating comparison';
    case 'generate_report':
      return 'Generating report';
    // System actions
    case 'load_skill':
      return 'Loading guidance';
    case 'create_system_from_template':
      return 'Creating system';
    case 'create_systems_batch':
      return 'Creating systems';
    case 'set_system_params':
      return 'Updating system';
    case 'set_solar_generator':
      return 'Configuring solar';
    case 'set_wind_generator':
      return 'Configuring wind';
    case 'set_storage_generator':
      return 'Configuring storage';
    case 'set_thermal_generator':
      return 'Configuring thermal';
    case 'set_pathway_powerplant':
      return 'Linking pathway powerplant';
    case 'delete_generator':
      return 'Removing generator';
    case 'run_system':
      return 'Running optimization';
    case 'get_system_results':
      return 'Reading results';
    case 'compare_systems':
      return 'Comparing systems';
    default:
      return actionType.replace(/_/g, ' ');
  }
}

function groupActions(actions) {
  const groups = [];
  for (const action of actions) {
    const last = groups[groups.length - 1];
    if (last && last.actionType === action.actionType) {
      last.items.push(action);
    } else {
      groups.push({ actionType: action.actionType, items: [action] });
    }
  }
  return groups;
}

function ActionProgress({ actions }) {
  const visible = actions.filter(a => PROGRESS_ACTIONS.has(a.actionType));
  if (visible.length === 0) return null;

  const groups = groupActions(visible);

  return (
    <div className="py-1 pl-0.5 space-y-1.5">
      {groups.map((group, i) => {
        const { actionType, items } = group;
        const count = items.length;
        const hasError = items.some(a => a.data?.error);
        const allPending = items.every(a => a.status === 'pending');
        const allDone = !hasError && items.every(a => a.status === 'done');
        const inProgress = !allPending && !allDone;

        const baseLabel =
          count > 1 ? (PLURAL_LABEL[actionType] ?? progressLabel(actionType)) : progressLabel(actionType);

        const downloadUrl =
          actionType === 'generate_report' ? items.find(a => a.data?.download_url)?.data?.download_url : null;

        return (
          <div key={i} className="flex items-center gap-2">
            <div className="shrink-0 w-4 h-4 flex items-center justify-center">
              {hasError ? (
                <span className="text-red-500 text-sm leading-none">✗</span>
              ) : allPending ? (
                <span className="text-slate-300 text-sm leading-none">○</span>
              ) : inProgress ? (
                <span className="loading loading-spinner loading-xs text-slate-400" />
              ) : (
                <span className="text-emerald-500 text-sm leading-none">✓</span>
              )}
            </div>

            <div className="flex flex-col min-w-0 gap-0.5">
              <div className="flex items-center gap-1.5">
                <span className={`text-xs leading-snug ${hasError ? 'text-red-500' : 'text-slate-500'}`}>
                  {baseLabel}
                </span>
                {count > 1 && <span className="text-[10px] text-slate-400 tabular-nums">x{count}</span>}
              </div>
              {downloadUrl && (
                <a
                  href={downloadUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-slate-500 underline decoration-slate-300 hover:decoration-slate-500 leading-tight"
                >
                  Download report
                </a>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

ActionProgress.propTypes = {
  actions: PropTypes.arrayOf(
    PropTypes.shape({
      actionType: PropTypes.string.isRequired,
      status: PropTypes.string,
      data: PropTypes.object,
    }),
  ).isRequired,
};

// ---------------------------------------------------------------------------
// Thinking bubble (collapsible, dev only)
// ---------------------------------------------------------------------------

function ThinkingBubble({ content }) {
  const [open, setOpen] = useState(false);
  if (!content) return null;

  return (
    <div className="mt-0.5 mb-1 text-[11px]">
      <button
        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-violet-50 text-violet-400 hover:text-violet-600 font-mono transition-colors"
        onClick={() => setOpen(o => !o)}
      >
        <span>{open ? '▾' : '▸'}</span>
        <span>thinking</span>
      </button>
      {open && (
        <pre className="mt-1 p-2 rounded bg-violet-50/50 text-violet-500 whitespace-pre-wrap text-[11px] leading-relaxed max-h-60 overflow-y-auto">
          {content}
        </pre>
      )}
    </div>
  );
}

function DebugStats({ stats }) {
  const [open, setOpen] = useState(false);
  if (!stats) return null;

  return (
    <div className="text-[10px] font-mono text-slate-400 px-1">
      <button className="hover:text-slate-600 transition-colors" onClick={() => setOpen(o => !o)}>
        <span>{open ? '▾' : '▸'}</span> {stats.rounds}r · {stats.elapsed_s?.toFixed(1)}s · {stats.total_errors}err
      </button>
      {open && (
        <div className="mt-1 p-2 rounded bg-slate-50 text-slate-500 space-y-0.5">
          <div>model: {stats.model}</div>
          <div>input tokens: {stats.input_tokens?.toLocaleString?.() ?? stats.input_tokens}</div>
          <div>output tokens: {stats.output_tokens?.toLocaleString?.() ?? stats.output_tokens}</div>
          <div>cache read: {stats.cache_read_tokens?.toLocaleString?.() ?? stats.cache_read_tokens}</div>
          <div>cache write: {stats.cache_write_tokens?.toLocaleString?.() ?? stats.cache_write_tokens}</div>
          {stats.round_details?.map((r, i) => (
            <div key={i} className="pl-2 border-l border-slate-200">
              round {r.round}: {r.tools?.join(', ') || 'text'} · {r.llm_time_s?.toFixed(1)}s llm ·{' '}
              {r.tool_time_s?.toFixed(1)}s tools
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

ThinkingBubble.propTypes = {
  content: PropTypes.string,
};

DebugStats.propTypes = {
  stats: PropTypes.shape({
    rounds: PropTypes.number,
    elapsed_s: PropTypes.number,
    total_errors: PropTypes.number,
    model: PropTypes.string,
    input_tokens: PropTypes.number,
    output_tokens: PropTypes.number,
    cache_read_tokens: PropTypes.number,
    cache_write_tokens: PropTypes.number,
    round_details: PropTypes.arrayOf(
      PropTypes.shape({
        round: PropTypes.number,
        tools: PropTypes.arrayOf(PropTypes.string),
        llm_time_s: PropTypes.number,
        tool_time_s: PropTypes.number,
      }),
    ),
  }),
};

// ---------------------------------------------------------------------------
// Message bubble
// ---------------------------------------------------------------------------

function CopyMessageButton({ content, className }) {
  const handleCopy = async e => {
    e.stopPropagation();
    if (!content) return;
    try {
      await navigator.clipboard.writeText(content);
      toast.success('Copied', { autoClose: 1500 });
    } catch {
      toast.error('Copy failed');
    }
  };
  return (
    <button
      type="button"
      onClick={handleCopy}
      className={`opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity p-1 rounded hover:bg-slate-200 ${className ?? ''}`}
      title="Copy message"
      aria-label="Copy message"
    >
      <DocumentDuplicateIcon className="w-3.5 h-3.5 text-slate-500" />
    </button>
  );
}

CopyMessageButton.propTypes = {
  content: PropTypes.string,
  className: PropTypes.string,
};

function MessageBubble({ message, onNavigate }) {
  if (message.role === 'user') {
    return (
      <div className="group flex justify-end items-start gap-1">
        <CopyMessageButton content={message.content} className="mt-2" />
        <div className="bg-slate-800 text-white rounded-2xl rounded-tr-sm px-4 py-2.5 max-w-[85%] text-sm leading-relaxed">
          {message.content}
        </div>
      </div>
    );
  }

  return (
    <div className="group relative py-0.5 text-sm leading-relaxed text-slate-800">
      <AgentMarkdown content={message.content} onNavigate={onNavigate} />
      <CopyMessageButton
        content={message.content}
        className="absolute top-0 right-0"
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Empty state
// ---------------------------------------------------------------------------

MessageBubble.propTypes = {
  message: PropTypes.shape({
    role: PropTypes.string.isRequired,
    content: PropTypes.string,
  }).isRequired,
  onNavigate: PropTypes.func,
};

// Starter prompts shown when the conversation is empty. Click populates
// the input rather than auto-sending so the user can edit before submitting.
// Aim for variety across the builder's capabilities: build from scratch,
// build + compare, read existing results, modify + rerun.
const STARTER_PROMPTS = [
  'Build a green hydrogen pathway in Texas with dedicated solar at 1,000 kg/day',
  'Compare blue vs green hydrogen for the Gulf Coast',
  'Show the LCA breakdown for my pathway — which scope dominates?',
  'Drop the electrolyzer CAPEX by 20 % and rerun',
];

function EmptyState({ onSelectPrompt }) {
  return (
    <div className="flex flex-col items-center justify-center h-full text-center px-6 pb-8 select-none">
      <p className="font-semibold text-slate-700 mb-1">Sesame AI</p>
      <p className="text-xs text-slate-400 leading-relaxed max-w-xs">
        Ask about this project, set parameters, run analyses, or create new pathways.
      </p>
      <div className="mt-5 space-y-1.5 text-xs text-left w-full max-w-xs">
        {STARTER_PROMPTS.map(prompt => (
          <button
            key={prompt}
            type="button"
            onClick={() => onSelectPrompt(prompt)}
            className="block w-full bg-white border border-stone-200 rounded px-3 py-1.5 text-slate-500 text-left hover:bg-slate-50 hover:border-stone-300 hover:text-slate-700 transition-colors cursor-pointer"
          >
            {prompt}
          </button>
        ))}
      </div>
    </div>
  );
}

EmptyState.propTypes = {
  onSelectPrompt: PropTypes.func.isRequired,
};

// ---------------------------------------------------------------------------
// Main panel
// ---------------------------------------------------------------------------

const HEIGHT_PRESETS = [
  { key: 'default', height: '75vh' },
  { key: 'full', height: '100vh' },
];

function AgentPanel() {
  const isAgentEnabled = useIsFeatureEnabled('agent');
  const {
    isOpen,
    isStreaming,
    messages,
    pendingActions,
    streamingText,
    _nextTextIsNewBubble,
  } = useAgentStore();
  const { projectId } = useParams();
  const matches = useMatches();
  const messagesEndRef = useRef(null);
  const scrollContainerRef = useRef(null);
  // Tracks whether the user has manually scrolled away from the bottom of
  // the message list. While true we suppress the auto-scroll-to-bottom on
  // streaming updates so the user can read history mid-turn. Reset to false
  // when the user sends a new message (their action signals re-engagement).
  // Stored in a ref to avoid re-rendering on every scroll event.
  const userScrolledUpRef = useRef(false);
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const deferredNavRef = useRef(null);

  const [heightPreset, setHeightPreset] = useState(() => localStorage.getItem('sesame-agent-height') ?? 'full');
  const panelHeight = HEIGHT_PRESETS.find(p => p.key === heightPreset)?.height ?? '100vh';
  const toggleHeight = () => {
    const next = heightPreset === 'full' ? 'default' : 'full';
    setHeightPreset(next);
    localStorage.setItem('sesame-agent-height', next);
  };
  const isFull = heightPreset === 'full';

  const MIN_WIDTH = 420;
  const MAX_WIDTH = 900;
  const [panelWidth, setPanelWidth] = useState(() => parseInt(localStorage.getItem('sesame-agent-width') ?? '560', 10));
  const isDraggingRef = useRef(false);

  const handleResizeMouseDown = e => {
    e.preventDefault();
    isDraggingRef.current = true;
    const startX = e.clientX;
    const startW = panelWidth;

    const onMouseMove = ev => {
      const delta = startX - ev.clientX;
      const newW = Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, startW + delta));
      setPanelWidth(newW);
    };
    const onMouseUp = () => {
      isDraggingRef.current = false;
      localStorage.setItem('sesame-agent-width', String(panelWidth));
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };

  useEffect(() => {
    if (projectId) agentActions.syncProject(projectId);
  }, [projectId]);

  useEffect(() => {
    if (!userScrolledUpRef.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, pendingActions, streamingText]);

  // Track whether the user is at (or near) the bottom of the scroll
  // container. 50 px tolerance covers anti-aliasing / subpixel drift after
  // a programmatic scrollIntoView lands. Updated on every scroll event;
  // the auto-scroll effect above reads it.
  const handleScroll = () => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    userScrolledUpRef.current = distanceFromBottom > 50;
  };

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 150);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isStreaming && deferredNavRef.current) {
      navigate(deferredNavRef.current);
      deferredNavRef.current = null;
    }
  }, [isStreaming, navigate]);

  // Populate the input with a starter prompt instead of auto-sending so
  // the user can tweak it (location, scale, units, etc.) before submitting.
  const handleSelectPrompt = prompt => {
    const el = inputRef.current;
    if (!el) return;
    el.value = prompt;
    autoResize();
    el.focus();
    el.setSelectionRange(prompt.length, prompt.length);
  };

  const handleSubmit = e => {
    e.preventDefault();
    const value = inputRef.current?.value?.trim();
    if (!value || isStreaming || !projectId) return;
    inputRef.current.value = '';
    autoResize();
    deferredNavRef.current = null;
    // User sending a new message means they want to follow the next turn —
    // reset the scroll-suppress flag so the auto-scroll effect resumes.
    userScrolledUpRef.current = false;
    agentActions.sendMessage({
      projectId,
      message: value,
      navigationContext: buildNavigationContext(matches),
      onAction: (actionType, data) => {
        if (PATHWAY_MUTATING_ACTIONS.has(actionType) && projectId) {
          getPathways(projectId).then(res => resourcesActions.setPathways(res.data));
        }

        if (
          (actionType === 'create_pathway_from_template' || actionType === 'build_pathway') &&
          data?.pathway_id &&
          data?.case_id &&
          projectId
        ) {
          deferredNavRef.current = `/projects/${projectId}/build/pathways/${data.pathway_id}/${data.case_id}`;
        }

        if (actionType === 'set_node_params' && data?.case_id && data?.node_id) {
          analysisStatusActions.markAsStale();
          const nodeState = useNodeStore.getState();
          if (nodeState?.caseNode?.node_id === data.node_id) {
            const pathwayId = usePathwayStore.getState()?.id;
            if (pathwayId) {
              nodeActions.init(pathwayId, data.case_id, data.node_id);
            }
          }
        }

        if (actionType === 'set_anchor' && data?.case_id) {
          analysisStatusActions.markAsStale();
          getCase(data.case_id).then(res => caseActions.init(res.data));
        }

        if ((actionType === 'balance_case' || actionType === 'analyze_case') && data?.case_id) {
          getCase(data.case_id).then(res => caseActions.init(res.data));
        }

        if (actionType === 'create_comparison' && data?.comparison_id && projectId) {
          getComparisons(projectId).then(res => {
            resourcesActions.setResources({ comparisons: res.data });
          });
          deferredNavRef.current = `/projects/${projectId}/comparisons/${data.comparison_id}`;
        }

        // System mutations: refresh the systems list in resourcesStore so the
        // sidebar reflects creates/changes immediately. For writes against a
        // currently-open system, refresh systemStore too so the form view
        // doesn't lag the agent's edits.
        if (SYSTEM_MUTATING_ACTIONS.has(actionType) && projectId) {
          getSystems(projectId).then(res => resourcesActions.setSystems(res.data));
          const openSystemId = useSystemStore.getState()?.id;
          if (openSystemId && data?.system_id && openSystemId === data.system_id) {
            getSystem(data.system_id).then(res => {
              systemActions.init(res.data, res.data.param_values);
            });
          }
        }

        // After creating a single system, deferred-navigate to its page.
        if (actionType === 'create_system_from_template' && data?.system_id && projectId) {
          deferredNavRef.current = `/projects/${projectId}/build/systems/${data.system_id}`;
        }
      },
    });
  };

  const autoResize = () => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    const max = 160;
    const clamped = Math.min(el.scrollHeight, max);
    el.style.height = `${clamped}px`;
    el.style.overflowY = el.scrollHeight > max ? 'auto' : 'hidden';
  };

  const handleKeyDown = e => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const showThinkingDots = isStreaming && pendingActions.length === 0;

  // True when every tool in the current round has finished (or errored).
  // The agent is now generating its final post-tool text.
  const allActionsSettled =
    pendingActions.length > 0 &&
    pendingActions.every(a => a.status === 'done' || a.data?.error);

  // Live bubble that shows the final post-tool text as it streams in.
  // Without this the user sees nothing for the 5-15 s of post-tool
  // generation because the underlying message bubble is marked
  // `intermediate` and hidden until onDone. Pre-tool text already streams
  // through the regular message bubble (pendingActions empty), so this
  // only renders for the post-tool round.
  const showStreamingText = isStreaming && allActionsSettled && !!streamingText;

  const showSummarizingDots =
    isStreaming &&
    !showThinkingDots &&
    !showStreamingText &&
    allActionsSettled &&
    _nextTextIsNewBubble;

  if (!isAgentEnabled) return null;

  return (
    <>
      {/* ── Floating toggle tab ─────────────────────────────────────────── */}
      <button
        className={`
          fixed top-1/2 -translate-y-1/2 right-0 z-[100]
          bg-slate-800 text-white
          px-1.5 py-5 rounded-l-lg shadow-lg
          hover:bg-slate-700 transition-all duration-200
          flex flex-col items-center gap-1.5
          ${isOpen ? 'opacity-0 pointer-events-none' : 'opacity-100'}
        `}
        onClick={agentActions.open}
        title="Open Sesame AI"
      >
        <span className="text-[10px] font-bold tracking-widest [writing-mode:vertical-rl]">AI</span>
      </button>

      {/* ── Slide-in panel ──────────────────────────────────────────────── */}
      <div
        className={`
          fixed bottom-0 right-0 bg-stone-50 shadow-2xl z-[100]
          flex flex-col border-l border-stone-200 rounded-tl-xl overflow-hidden
          transition-transform duration-300 ease-in-out
          ${isOpen ? 'translate-x-0' : 'translate-x-full'}
        `}
        style={{ height: panelHeight, width: panelWidth }}
      >
        {/* Drag handle (left edge) */}
        <div
          className="absolute left-0 top-0 bottom-0 w-1.5 cursor-col-resize hover:bg-slate-300/50 z-10"
          onMouseDown={handleResizeMouseDown}
        />
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-2 bg-slate-800 shrink-0">
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-white select-none">Sesame AI</span>
            <BetaBadge className="text-slate-300 border-slate-500" />
          </div>
          <div className="flex items-center gap-3">
            {isStreaming && (
              <button
                className="text-xs text-slate-300 hover:text-red-400 transition-colors"
                onClick={agentActions.stopStreaming}
              >
                Stop
              </button>
            )}
            {messages.length > 0 && (
              <button
                className="text-slate-300 hover:text-white transition-colors"
                onClick={() => {
                  agentActions.exportConversation();
                  toast.success('Conversation exported', { autoClose: 1500 });
                }}
                title="Export conversation as markdown"
                aria-label="Export conversation"
              >
                <ArrowDownTrayIcon className="w-3.5 h-3.5" />
              </button>
            )}
            {messages.length > 0 && (
              <button
                className="text-xs text-slate-300 hover:text-white transition-colors"
                onClick={agentActions.clearConversation}
              >
                New chat
              </button>
            )}
            <button
              className="text-slate-300 hover:text-white transition-colors"
              onClick={toggleHeight}
              title={isFull ? 'Shrink panel' : 'Expand panel'}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 16 16"
                fill="currentColor"
                className={`w-3.5 h-3.5 transition-transform ${isFull ? 'rotate-180' : ''}`}
              >
                <path
                  fillRule="evenodd"
                  d="M8 14a.75.75 0 01-.75-.75V4.612L4.29 7.77a.75.75 0 01-1.08-1.04l4.25-4.5a.75.75 0 011.08 0l4.25 4.5a.75.75 0 11-1.08 1.04L8.75 4.612V13.25A.75.75 0 018 14z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
            <button className="text-xs text-slate-300 hover:text-white transition-colors" onClick={agentActions.close}>
              Close
            </button>
          </div>
        </div>

        {/* Message list */}
        <div className="flex-1 relative min-h-0 bg-stone-50">
          <div
            ref={scrollContainerRef}
            onScroll={handleScroll}
            className="absolute inset-0 overflow-y-auto px-4 py-4 space-y-3"
          >
            {messages.length === 0 ? (
              <EmptyState onSelectPrompt={handleSelectPrompt} />
            ) : (
              <>
                {messages
                  .filter(msg => msg.role !== 'assistant' || !msg.intermediate)
                  .map((msg, i) =>
                    msg.role === 'actions' ? (
                      <ActionProgress key={i} actions={msg.actions} />
                    ) : msg.role === 'thinking' ? (
                      <ThinkingBubble key={i} content={msg.content} />
                    ) : (
                      <React.Fragment key={i}>
                        <MessageBubble message={msg} onNavigate={navigate} />
                        {msg.debugStats && <DebugStats stats={msg.debugStats} />}
                      </React.Fragment>
                    ),
                  )}
                {pendingActions.length > 0 && <ActionProgress actions={pendingActions} />}
              </>
            )}
            {showThinkingDots && (
              <div className="py-0.5">
                <span className="loading loading-dots loading-xs text-slate-300" />
              </div>
            )}
            {showSummarizingDots && (
              <div className="py-0.5">
                <span className="loading loading-dots loading-xs text-slate-300" />
              </div>
            )}
            {showStreamingText && (
              <MessageBubble
                message={{ role: 'assistant', content: streamingText }}
                onNavigate={navigate}
              />
            )}
            <div ref={messagesEndRef} />
            <div className="h-10" />
          </div>
          {messages.length > 0 && (
            <div className="absolute bottom-0 left-0 right-0 h-14 bg-gradient-to-t from-stone-50 to-transparent pointer-events-none" />
          )}
        </div>

        {/* Input area */}
        <form onSubmit={handleSubmit} className="shrink-0 px-3 pb-3 pt-1 bg-stone-50">
          <div className="flex items-end bg-white border border-stone-200 rounded-xl px-3 py-2 shadow-sm">
            {/*
              Textarea stays enabled while isStreaming so the user can draft
              their next message during the current turn (post-tool generation
              can be 10+ s of perceived blank time). The submit button stays
              disabled so they can't actually fire another request until the
              turn ends; handleSubmit also no-ops while isStreaming as a
              second line of defence.
            */}
            <textarea
              ref={inputRef}
              rows={2}
              className="flex-1 bg-transparent outline-none resize-none text-sm leading-snug placeholder:text-slate-400 overflow-hidden"
              placeholder={isStreaming ? 'Draft your next message…' : 'Ask a question or give an instruction…'}
              onKeyDown={handleKeyDown}
              onInput={autoResize}
            />
            <button
              type="submit"
              className="ml-2 p-1.5 rounded-lg bg-slate-800 text-white hover:bg-slate-700 disabled:opacity-30 disabled:hover:bg-slate-800 transition-colors shrink-0"
              disabled={isStreaming || !projectId}
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                <path
                  fillRule="evenodd"
                  d="M10 17a.75.75 0 01-.75-.75V5.612L5.29 9.77a.75.75 0 01-1.08-1.04l5.25-5.5a.75.75 0 011.08 0l5.25 5.5a.75.75 0 11-1.08 1.04l-3.96-4.158V16.25A.75.75 0 0110 17z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          </div>
          <p className="text-[10px] text-slate-400 mt-1.5 px-1">Enter to send · Shift+Enter for new line</p>
        </form>
      </div>
    </>
  );
}

export default AgentPanel;
